/**
 * Update Handler
 * Detects when new versions of the app are available and prompts users to update
 */

interface VersionInfo {
  version: string;
  buildTime: string;
  gitCommit: string;
  gitBranch?: string;
  buildNumber?: string;
  environment?: string;
}

interface UpdateHandlerOptions {
  checkInterval?: number; // in milliseconds
  enableNotifications?: boolean;
  onUpdateAvailable?: (newVersion: VersionInfo, currentVersion: VersionInfo) => void;
  onUpdateError?: (error: Error) => void;
}

class UpdateHandler {
  private checkInterval: number;
  private enableNotifications: boolean;
  private onUpdateAvailable?: (newVersion: VersionInfo, currentVersion: VersionInfo) => void;
  private onUpdateError?: (error: Error) => void;
  private intervalId?: number;
  private currentVersion?: VersionInfo;

  constructor(options: UpdateHandlerOptions = {}) {
    this.checkInterval = options.checkInterval ?? 5 * 60 * 1000; // 5 minutes
    this.enableNotifications = options.enableNotifications ?? true;
    this.onUpdateAvailable = options.onUpdateAvailable;
    this.onUpdateError = options.onUpdateError;
  }

  /**
   * Start checking for updates
   */
  async start(): Promise<void> {
    try {
      // Get current version
      this.currentVersion = await this.getCurrentVersion();
      
      // Check immediately
      await this.checkForUpdates();
      
      // Set up periodic checks
      this.intervalId = window.setInterval(() => {
        this.checkForUpdates().catch(error => {
          console.warn('Update check failed:', error);
          if (this.onUpdateError) {
            this.onUpdateError(error);
          }
        });
      }, this.checkInterval);

      console.log('Update handler started, checking every', this.checkInterval / 1000, 'seconds');
    } catch (error) {
      console.error('Failed to start update handler:', error);
      if (this.onUpdateError) {
        this.onUpdateError(error as Error);
      }
    }
  }

  /**
   * Stop checking for updates
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
      console.log('Update handler stopped');
    }
  }

  /**
   * Get current version from localStorage or fetch from server
   */
  private async getCurrentVersion(): Promise<VersionInfo> {
    const stored = localStorage.getItem('app-version-info');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.warn('Failed to parse stored version info:', error);
      }
    }

    // Fetch from server if not in localStorage
    return await this.fetchVersionInfo();
  }

  /**
   * Fetch version info from server
   */
  private async fetchVersionInfo(): Promise<VersionInfo> {
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch version info: ${response.status}`);
    }

    const versionInfo = await response.json();
    
    // Store in localStorage
    localStorage.setItem('app-version-info', JSON.stringify(versionInfo));
    
    return versionInfo;
  }

  /**
   * Check for updates
   */
  async checkForUpdates(): Promise<boolean> {
    try {
      const newVersion = await this.fetchVersionInfo();
      
      if (!this.currentVersion) {
        this.currentVersion = newVersion;
        return false;
      }

      // Compare versions
      const hasUpdate = this.hasVersionChanged(this.currentVersion, newVersion);
      
      if (hasUpdate) {
        console.log('New version detected:', {
          current: this.currentVersion,
          new: newVersion
        });

        if (this.onUpdateAvailable) {
          this.onUpdateAvailable(newVersion, this.currentVersion);
        }

        if (this.enableNotifications) {
          this.showUpdateNotification(newVersion);
        }

        // Update current version
        this.currentVersion = newVersion;
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Failed to check for updates:', error);
      if (this.onUpdateError) {
        this.onUpdateError(error as Error);
      }
      return false;
    }
  }

  /**
   * Check if version has changed
   */
  private hasVersionChanged(current: VersionInfo, latest: VersionInfo): boolean {
    // Compare by build time first (most reliable)
    if (current.buildTime !== latest.buildTime) {
      return true;
    }

    // Compare by git commit
    if (current.gitCommit !== latest.gitCommit) {
      return true;
    }

    // Compare by version number
    if (current.version !== latest.version) {
      return true;
    }

    // Compare by build number if available
    if (current.buildNumber && latest.buildNumber && current.buildNumber !== latest.buildNumber) {
      return true;
    }

    return false;
  }

  /**
   * Show update notification
   */
  private showUpdateNotification(newVersion: VersionInfo): void {
    // Create a simple notification banner
    const existingBanner = document.getElementById('update-notification-banner');
    if (existingBanner) {
      existingBanner.remove();
    }

    const banner = document.createElement('div');
    banner.id = 'update-notification-banner';
    banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
      color: white;
      padding: 12px 16px;
      text-align: center;
      z-index: 10000;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
    `;

    banner.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; gap: 16px; max-width: 1200px; margin: 0 auto;">
        <span>🚀 HEAT Training has been updated with new features!</span>
        <button id="update-refresh-btn" style="
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          transition: all 0.2s;
        ">Refresh Now</button>
        <button id="update-dismiss-btn" style="
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          cursor: pointer;
          font-size: 18px;
          padding: 4px;
          line-height: 1;
        ">×</button>
      </div>
    `;

    document.body.appendChild(banner);

    // Add event listeners
    const refreshBtn = document.getElementById('update-refresh-btn');
    const dismissBtn = document.getElementById('update-dismiss-btn');

    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        window.location.reload();
      });

      // Add hover effect
      refreshBtn.addEventListener('mouseenter', () => {
        refreshBtn.style.background = 'rgba(255,255,255,0.3)';
      });
      refreshBtn.addEventListener('mouseleave', () => {
        refreshBtn.style.background = 'rgba(255,255,255,0.2)';
      });
    }

    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        banner.remove();
      });

      dismissBtn.addEventListener('mouseenter', () => {
        dismissBtn.style.color = 'white';
      });
      dismissBtn.addEventListener('mouseleave', () => {
        dismissBtn.style.color = 'rgba(255,255,255,0.8)';
      });
    }

    // Auto-dismiss after 30 seconds
    setTimeout(() => {
      if (banner.parentNode) {
        banner.remove();
      }
    }, 30000);
  }

  /**
   * Force check for updates
   */
  async forceCheck(): Promise<boolean> {
    return await this.checkForUpdates();
  }

  /**
   * Get current version info
   */
  getCurrentVersionInfo(): VersionInfo | undefined {
    return this.currentVersion;
  }
}

// Create singleton instance
export const updateHandler = new UpdateHandler({
  checkInterval: 5 * 60 * 1000, // Check every 5 minutes
  enableNotifications: true,
  onUpdateAvailable: (newVersion, currentVersion) => {
    console.log('Update available:', { newVersion, currentVersion });
    
    // Track in analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', 'app_update_available', {
        current_version: currentVersion.version,
        new_version: newVersion.version,
        current_build_time: currentVersion.buildTime,
        new_build_time: newVersion.buildTime
      });
    }
  },
  onUpdateError: (error) => {
    console.warn('Update check error:', error);
    
    // Track errors in analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', 'app_update_check_error', {
        error_message: error.message
      });
    }
  }
});

export default updateHandler;
